import {
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { setTokens, logout } from '../slices/authSlice';
import { getRefreshToken } from '../../lib/auth';
import type { RootState } from '../store';
import i18n from '../../i18n';

export interface RefreshTokenResponse {
  token: string;
  refreshToken?: string;
}

const BASE_URL = process.env.REACT_APP_API_URL || '';

export const baseQueryWithoutAuth = fetchBaseQuery({
  baseUrl: BASE_URL,
});

const createBaseQuery = (baseUrl: string = BASE_URL) => {
  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('accept-language', i18n.language);
      return headers;
    },
  });
};

const baseQuery = createBaseQuery();

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const refreshToken = getRefreshToken();

    if (refreshToken) {
      try {
        const refreshResult = await baseQueryWithoutAuth(
          {
            url: '/v1/auth/admin/refresh-token?refreshToken=' + refreshToken,
            method: 'POST',
          },
          api,
          extraOptions
        );

        if (refreshResult.data) {
          const { token, refreshToken: newRefreshToken } =
            refreshResult.data as RefreshTokenResponse;

          // Store the new tokens
          api.dispatch(setTokens({ token, refreshToken: newRefreshToken }));

          // Retry the original query with the new token
          result = await baseQuery(args, api, extraOptions);
        } else {
          // Refresh failed, logout the user
          api.dispatch(logout());
        }
      } catch (error) {
        api.dispatch(logout());
      }
    } else {
      api.dispatch(logout());
    }
  }

  return result;
};

// Helper function to create baseQueryWithReauth with custom base URL
export const createBaseQueryWithReauth = (
  customBaseUrl?: string
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  const customBaseQuery = createBaseQuery(customBaseUrl || BASE_URL);

  return async (args, api, extraOptions) => {
    let result = await customBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      // Refresh token is stored in cookies
      const refreshToken = getRefreshToken();

      if (refreshToken) {
        try {
          // Try to refresh the token using baseQueryWithoutAuth (no Authorization header needed)
          const refreshResult = await baseQueryWithoutAuth(
            {
              url: '/v1/auth/admin/refresh-token?refreshToken=' + refreshToken,
              method: 'POST',
            },
            api,
            extraOptions
          );

          if (refreshResult.data) {
            const { token, refreshToken: newRefreshToken } =
              refreshResult.data as RefreshTokenResponse;

            // Store the new tokens
            api.dispatch(setTokens({ token, refreshToken: newRefreshToken }));

            // Retry the original query with the new token
            result = await customBaseQuery(args, api, extraOptions);
          } else {
            // Refresh failed, logout the user
            api.dispatch(logout());
          }
        } catch (error) {
          api.dispatch(logout());
        }
      } else {
        api.dispatch(logout());
      }
    }

    return result;
  };
};

